import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Download,
  ExternalLink,
  FileText,
  DollarSign,
  Building,
  Package,
  User
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAuthContext } from '@/contexts/AuthContext';
import { sanitizeHTML } from '@/utils/htmlSanitizer';

// Interface para cotação do Hubspot - atualizado com os campos reais
interface HubspotQuote {
  id: string;
  deal_id: string;
  hs_object_id?: string;
  hs_quote_number?: string;
  hs_status?: string;
  hs_quote_amount?: number;
  hs_currency?: string;
  hs_createdate?: string;
  hs_expiration_date?: string;
  hs_quote_link?: string;
  hs_pdf_download_link?: string;
  hs_terms?: string;
  hs_sender_firstname?: string;
  hs_sender_lastname?: string;
  hs_sender_email?: string;
  hs_sender_phone?: string;
  hs_language?: string;
  hs_locale?: string;
  hs_comments?: string;
  hs_lastmodifieddate?: string;
  hs_all_owner_ids?: string;
  hs_associated_deal_owner_id?: string;
  hs_created_by_user_id?: string;
  hs_esign_date?: string;
  hs_esign_enabled?: boolean;
  hs_esign_num_signers_completed?: number;
  hs_esign_num_signers_required?: number;
  hs_esign_verification_method?: string;
  hs_locked?: boolean;
  hs_num_associated_deals?: number;
  hs_pdf_generation_status?: string;
  hs_quote_owner_id?: string;
  hs_tcv?: number;
  hs_updated_by_user_id?: string;
}

// Interface para oportunidade do Hubspot
interface HubspotDeal {
  id: string;
  dealname?: string;
  amount?: number;
  dealstage?: string;
  deal_currency_code?: string;
  hs_createdate?: string;
  hs_lastmodifieddate?: string;
  owner_name?: string;
  company_id?: string;
  customer_name?: string;
  customer?: {
      id: string;
      name: string;
      business_identification_number?: string;
      legal_name?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
    industry?: string;
    phone?: string;
  };
}

// Interface para itens da cotação do Hubspot
interface HubspotLineItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  amount: number;
  discount: number;
  description?: string;
  createdate?: string;
  hs_product_currency_code?: string;
  deal_id?: string;
  quote_id?: string;
}

// Interface para templates de contrato
interface ContractTemplate {
  id: string;
  name: string;
  description: string;
  template_type: string;
  template_html: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Mapeamento de status para exibição
const statusLabels: Record<string, string> = {
  'APPROVED': 'Aprovada',
  'PENDING': 'Pendente',
  'REJECTED': 'Rejeitada',
  'SENT': 'Enviada',
  'DRAFT': 'Rascunho',
  'EXPIRED': 'Expirada'
};

// Cores para cada status
const statusColors: Record<string, string> = {
  'APPROVED': 'bg-green-100 text-green-800',
  'PENDING': 'bg-blue-100 text-blue-800',
  'REJECTED': 'bg-red-100 text-red-800',
  'SENT': 'bg-purple-100 text-purple-800',
  'DRAFT': 'bg-gray-100 text-gray-800',
  'EXPIRED': 'bg-amber-100 text-amber-800'
};

const ProposalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();

  const [quote, setQuote] = useState<HubspotQuote | null>(null);
  const [dealData, setDealData] = useState<HubspotDeal | null>(null);
  const [lineItems, setLineItems] = useState<HubspotLineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [contractTitle, setContractTitle] = useState<string>('');
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para controle da UI
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isItemsOpen, setIsItemsOpen] = useState(true);

  // Verificar se o ID é um UUID válido ou um ID numérico do HubSpot
  const isValidId = useCallback(() => {
    // Verificar se é um UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    // Verificar se é um ID numérico (IDs do HubSpot geralmente são numéricos)
    const numericRegex = /^\d+$/;
    
    return uuidRegex.test(id || '') || numericRegex.test(id || '');
  }, [id]);

  useEffect(() => {
    const fetchQuoteData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Verificar se o ID é válido
        if (!isValidId()) {
          throw new Error("ID de proposta inválido");
        }
        
        // Primeiro, tente buscar como uma cotação do Hubspot
        try {
          if (!import.meta.env.PROD) console.log(`Fetching quote data for ID: ${id}`);
          
          // Verificando primeiro se é uma cotação do Hubspot
          const { data: hubspotQuote, error: hubspotError } = await supabase
            .from('hubspotquotes' as any)
            .select('*') // Use * to get all available fields instead of explicitly naming them
            .eq('id', id)
            .single();

          if (hubspotError) {
            console.error('Error fetching Hubspot quote:', hubspotError);
            
            // Se não encontrar no Hubspot, verificamos se é uma proposta interna (UUID)
            if (hubspotError.code === 'PGRST116') {
              // Tentar buscar como proposta interna (implementação futura)
              console.error('Proposta interna não implementada ainda');
              toast({
                title: 'Cotação não encontrada',
                description: 'O ID fornecido não corresponde a nenhuma cotação do Hubspot.',
                variant: 'destructive',
              });
              setIsLoading(false);
              return;
            }
            throw hubspotError;
          }

          if (!import.meta.env.PROD) console.log('Quote found:', hubspotQuote);
          
          // Quando encontrarmos a cotação do Hubspot, buscamos informações da oportunidade relacionada
          if (hubspotQuote && (hubspotQuote as any).deal_id) {
            if (!import.meta.env.PROD) console.log(`Fetching deal data for deal ID: ${(hubspotQuote as any).deal_id}`);
            
            const { data: dealData, error: dealError } = await supabase
              .from('hubspotopportunities' as any)
              .select(`
                id,
                dealname,
                amount,
                dealstage,
                deal_currency_code,
                hs_createdate,
                hs_lastmodifieddate,
                owner_name,
                company_id,
                customer_name
              `)
              .eq('id', (hubspotQuote as any).deal_id)
              .single();

            if (dealError) {
              console.error('Error fetching deal data:', dealError);
            } else if (dealData) {
              if (!import.meta.env.PROD) console.log('Deal data found:', dealData);
              
              // Create a customer object from the data we have
              const customerData = {
                id: (dealData as any).company_id || '',
                name: (dealData as any).customer_name || 'Cliente sem nome',
                business_identification_number: '',
                legal_name: (dealData as any).customer_name || '',
                address: '',
                city: '',
                state: '',
                zip: '',
                industry: '',
                phone: ''
              };
              
              // Add the customer data to the deal data
              const dealWithCustomer = {
                ...(dealData as any),
                customer: customerData
              };
              
              setDealData(dealWithCustomer as HubspotDeal);
            }
          }

          setQuote(hubspotQuote as unknown as HubspotQuote);
          
          // Buscando os itens relacionados a esta cotação
          if (hubspotQuote) {
            try {
              if (!import.meta.env.PROD) console.log(`Fetching line items for deal ID: ${(hubspotQuote as any).deal_id}`);
              
              const { data: lineItemsData, error: lineItemsError } = await supabase
                .from('hubspotlineitems' as any)
                .select('*')
                .eq('deal_id', (hubspotQuote as any).deal_id)
                .order('id', { ascending: true });
                
              if (lineItemsError) {
                console.error('Error fetching line items:', lineItemsError);
              } else if (lineItemsData && lineItemsData.length > 0) {
                if (!import.meta.env.PROD) console.log(`Found ${lineItemsData.length} line items:`, lineItemsData);
                
                // Map API response to our interface
                const formattedLineItems: HubspotLineItem[] = (lineItemsData as any[]).map(item => ({
                  id: item.id || '',
                  name: item.name || 'Sem nome',
                  quantity: parseFloat(item.quantity) || 0,
                  price: parseFloat(item.price) || 0,
                  amount: parseFloat(item.amount) || 0,
                  discount: parseFloat(item.discount) || 0,
                  description: item.description,
                  createdate: item.createdate,
                  hs_product_currency_code: item.hs_product_currency_code,
                  deal_id: item.deal_id,
                  quote_id: (hubspotQuote as any).id
                }));
                
                setLineItems(formattedLineItems);
              } else {
                if (!import.meta.env.PROD) console.log('No line items found for this quote');
              }
            } catch (error) {
              console.error('Error processing line items:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching quote data:', error);
          toast({
            title: 'Erro ao carregar cotação',
            description: 'Não foi possível carregar os dados da cotação.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error fetching quote data:', error);
        toast({
          title: 'Erro ao carregar cotação',
          description: 'Não foi possível carregar os dados da cotação.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuoteData();
  }, [id, toast]);

  useEffect(() => {
    // Carregar templates de contrato
    const fetchContractTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from('contract_templates')
          .select('*')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        if (data) setContractTemplates(data);
      } catch (error) {
        console.error('Erro ao carregar templates de contrato:', error);
      }
    };

    fetchContractTemplates();
  }, []);

  const handleBackClick = () => {
    // Se temos os dados da oportunidade, voltamos para lá
    if (dealData) {
      navigate(`/business-opportunities/${dealData.id}`);
    } else {
      // Caso contrário, voltamos para a lista de oportunidades
      navigate('/business-opportunities');
    }
  };

  const formatSenderName = () => {
    const firstName = quote?.hs_sender_firstname || '';
    const lastName = quote?.hs_sender_lastname || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return 'N/A';
  };

  const openQuoteUrl = () => {
    if (quote?.hs_quote_link) {
      window.open(quote.hs_quote_link, '_blank');
    } else {
      toast({
        title: 'Link indisponível',
        description: 'Esta cotação não possui um link direto para visualização.',
        variant: 'destructive',
      });
    }
  };

  const downloadPdf = () => {
    if (quote?.hs_pdf_download_link) {
      window.open(quote.hs_pdf_download_link, '_blank');
    } else {
      toast({
        title: 'PDF não disponível',
        description: 'Esta cotação não possui PDF para download.',
        variant: 'destructive',
      });
    }
  };

  // Helper function to safely get quote title
  const getQuoteTitle = () => {
    // Try to use any available identifiers for the quote
    if (quote?.hs_quote_number) {
      return `Cotação #${quote.hs_quote_number}`;
    } else if (dealData?.dealname) {
      return `Cotação para ${dealData.dealname}`;
    } else {
      return `Cotação #${quote?.id || ''}`;
    }
  };

  const handleOpenContractModal = () => {
    // Definir título padrão do contrato baseado na cotação
    if (quote && dealData) {
      // Use dealname instead of relying on hs_title which doesn't exist
      setContractTitle(`Contrato - ${dealData.dealname || 'Oportunidade'} - ${quote.hs_quote_number || quote.id}`);
    }
    setShowContractModal(true);
  };

  const handleCreateContract = async () => {
    if (!selectedTemplate || !contractTitle || !quote || !dealData?.customer?.id) {
      toast({
        title: 'Informações insuficientes',
        description: 'Por favor, preencha todos os campos obrigatórios.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsCreatingContract(true);

    try {
      // To debug the issue, let's log the actual types of all values
      if (!import.meta.env.PROD) {
        console.log('DEBUG - Quote ID:', quote.id);
        console.log('DEBUG - Quote ID type:', typeof quote.id);
        console.log('DEBUG - Customer ID:', dealData.customer.id);
        console.log('DEBUG - Customer ID type:', typeof dealData.customer.id);
      }
      
      // Create contract data with a dummy UUID
      // Using string literal for UUID to ensure it's exactly the right format
      const contractData = {
        // Valid UUID format (version 4)
        proposal_id: "00000000-0000-0000-0000-000000000000",
        customer_id: dealData.customer.id,
        title: contractTitle,
        template_type: contractTemplates.find(t => t.id === selectedTemplate)?.template_type || 'services',
        status: 'draft',
        start_date: new Date().toISOString().split('T')[0],
        total_value: quote.hs_quote_amount || 0,
        currency: quote.hs_currency || 'BRL'
      };
      
      // Add quote_id separately to ensure it doesn't interfere with the main insert
      // This will help us determine if quote_id is causing the issue
      if (!import.meta.env.PROD) console.log('Contract data (without quote_id):', JSON.stringify(contractData));
      
      // Try inserting without the quote_id first
      const { data, error } = await supabase
        .from('business_contracts')
        .insert([contractData])
        .select();
      
      if (error) {
        console.error('Erro detalhado ao criar contrato:', error);
        throw error;
      }

      if (!import.meta.env.PROD) console.log('Contract created successfully:', data);
      
      // Now update the contract with the quote_id in a separate operation
      if (data && data[0]) {
        const contractId = data[0].id;
        
        // Update the contract with the quote_id
        const { error: updateError } = await supabase
          .from('business_contracts')
          .update({ quote_id: String(quote.id) } as any)
          .eq('id', contractId);
          
        if (updateError) {
          console.error('Erro ao atualizar quote_id:', updateError);
          // Don't throw here, we'll proceed with the contract anyway
        }
        
        setShowContractModal(false);
        toast({
          title: 'Contrato criado com sucesso',
          description: 'O contrato foi criado e está disponível para edição.',
        });

        // Navigate to the contract page
        navigate(`/contracts/${contractId}`);
      }
    } catch (error) {
      console.error('Erro ao criar contrato:', error);
      toast({
        title: 'Erro ao criar contrato',
        description: `Não foi possível criar o contrato: ${(error as any)?.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
    } finally {
      setIsCreatingContract(false);
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 py-6 w-full">
        <div className="flex justify-center items-center h-72">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="ml-4 text-lg text-muted-foreground">Carregando cotação...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 w-full">
        <div className="flex flex-col items-center justify-center space-y-4 py-16">
          <div className="bg-destructive/10 border border-destructive/20 text-destructive p-6 rounded-lg max-w-lg mx-auto text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h3 className="text-xl font-extrabold mb-3">Erro ao carregar proposta</h3>
            <p className="mb-4">{error}</p>
            <p className="text-sm mb-4">
              O ID fornecido não é válido ou a proposta não existe. Verifique se você está acessando a URL correta.
            </p>
            <Button variant="outline" onClick={() => navigate('/business-opportunities')} className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Oportunidades
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 py-6 w-full">
        <div className="flex flex-col items-center justify-center space-y-4 py-16">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg max-w-lg mx-auto text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-xl font-extrabold mb-3">Proposta não encontrada</h3>
            <p className="mb-4">
              Não foi possível encontrar uma proposta com o ID: {id}
            </p>
            <p className="text-sm mb-4">
              A proposta pode ter sido removida ou o ID fornecido está incorreto.
            </p>
            <Button variant="outline" onClick={() => navigate('/business-opportunities')} className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Oportunidades
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 w-full">
      {/* Header com ações */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleBackClick} className="h-9">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar
          </Button>
          
          <Badge className={`${quote?.hs_status ? statusColors[quote.hs_status] : 'bg-gray-100 text-gray-800'}`}>
            {quote?.hs_status ? statusLabels[quote.hs_status] || quote.hs_status : 'Desconhecido'}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          {quote.hs_pdf_download_link && (
            <Button variant="outline" size="sm" onClick={downloadPdf}>
              <Download className="h-4 w-4 mr-2" />
              Baixar PDF
            </Button>
          )}
          {quote.hs_quote_link && (
            <Button variant="outline" size="sm" onClick={openQuoteUrl}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Ver no HubSpot
            </Button>
          )}
          <Button onClick={handleOpenContractModal}>
            <FileText className="h-4 w-4 mr-2" />
            Gerar Contrato
          </Button>
        </div>
      </div>
      
      {/* Título e detalhes principais */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight mb-2">
          {getQuoteTitle()}
        </h1>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Calendar className="h-3.5 w-3.5" />
          <span>
            Criada em {
              quote.hs_createdate 
                ? new Date(quote.hs_createdate).toLocaleDateString() 
                : 'N/A'
            }
          </span>
          
          {quote.hs_expiration_date && (
            <>
              <span>•</span>
              <Calendar className="h-3.5 w-3.5" />
              <span>
                Expira em {new Date(quote.hs_expiration_date).toLocaleDateString()}
              </span>
            </>
          )}
          
          {dealData?.dealname && (
            <>
              <span>•</span>
              <Building className="h-3.5 w-3.5" />
              <span>
                {dealData.customer?.name || 'Cliente desconhecido'}
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Cards de overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Valor Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-extrabold text-muted-foreground">Valor Total</h3>
              <DollarSign className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-extrabold">
              {quote?.hs_quote_amount 
                ? formatCurrency(quote.hs_quote_amount, quote?.hs_currency || 'BRL') 
                : 'N/A'
              }
            </p>
          </CardContent>
        </Card>
        
        {/* Status Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-extrabold text-muted-foreground">Status</h3>
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-extrabold">
              {quote?.hs_status 
                ? statusLabels[quote.hs_status] || quote.hs_status 
                : 'Desconhecido'
              }
            </p>
            {quote?.hs_esign_enabled && (
              <div className="mt-2 text-xs text-muted-foreground">
                {quote?.hs_esign_num_signers_completed || 0} de {quote?.hs_esign_num_signers_required || 0} assinaturas
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Data Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-extrabold text-muted-foreground">Expiração</h3>
              <Calendar className="h-5 w-5 text-orange-500" />
            </div>
            <p className="text-2xl font-extrabold">
              {quote.hs_expiration_date 
                ? new Date(quote.hs_expiration_date).toLocaleDateString() 
                : 'Não definida'
              }
            </p>
          </CardContent>
        </Card>
        
        {/* Linked Deal Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-extrabold text-muted-foreground">Oportunidade</h3>
              <Building className="h-5 w-5 text-purple-500" />
            </div>
            <p className="text-2xl font-extrabold truncate" title={dealData?.dealname || ''}>
              {dealData?.dealname
                ? dealData.dealname.length > 18 
                  ? dealData.dealname.substring(0, 18) + '...'
                  : dealData.dealname
                : 'Não vinculada'
              }
            </p>
            {dealData && (
              <Button 
                variant="link" 
                className="p-0 h-auto text-xs text-muted-foreground"
                onClick={() => navigate(`/business-opportunities/${dealData.id}`)}
              >
                Ver detalhes
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Itens da Cotação */}
          {lineItems.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-xl flex items-center">
                    <Package className="mr-2 h-5 w-5 text-muted-foreground" />
                    Itens da Cotação
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                      <TableHead className="text-right">Preço Unitário</TableHead>
                      <TableHead className="text-right">Desconto</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-extrabold">{item.name}</div>
                          {item.description && (
                            <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.price, item?.hs_product_currency_code || quote?.hs_currency || 'BRL')}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.discount > 0 ? `${item.discount}%` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.amount, item?.hs_product_currency_code || quote?.hs_currency || 'BRL')}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} className="text-right font-extrabold">
                        Total da Cotação:
                      </TableCell>
                      <TableCell className="text-right font-extrabold">
                        {quote?.hs_quote_amount 
                          ? formatCurrency(quote.hs_quote_amount, quote?.hs_currency || 'BRL')
                          : formatCurrency(
                              lineItems.reduce((sum, item) => sum + (item.amount || 0), 0),
                              quote?.hs_currency || 'BRL'
                            )
                        }
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          
          {/* Detalhes da Cotação */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-xl flex items-center">
                <FileText className="mr-2 h-5 w-5 text-muted-foreground" />
                Detalhes da Cotação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Número da Cotação</h4>
                  <p className="font-extrabold">{quote.hs_quote_number || quote.id}</p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Status</h4>
                  <div>
                    <Badge className={`${quote.hs_status ? statusColors[quote.hs_status] : 'bg-gray-100 text-gray-800'}`}>
                      {quote.hs_status ? statusLabels[quote.hs_status] || quote.hs_status : 'Desconhecido'}
                    </Badge>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Data de Criação</h4>
                  <p className="font-extrabold">
                    {quote.hs_createdate 
                      ? new Date(quote.hs_createdate).toLocaleDateString()
                      : 'Não especificada'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Data de Expiração</h4>
                  <p className="font-extrabold">
                    {quote.hs_expiration_date 
                      ? new Date(quote.hs_expiration_date).toLocaleDateString()
                      : 'Não especificada'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Criado por</h4>
                  <p className="font-extrabold">{formatSenderName()}</p>
                  {quote.hs_sender_email && (
                    <p className="text-xs text-muted-foreground">{quote.hs_sender_email}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Última Atualização</h4>
                  <p className="font-extrabold">
                    {quote.hs_lastmodifieddate 
                      ? new Date(quote.hs_lastmodifieddate).toLocaleDateString()
                      : 'Não especificada'}
                  </p>
                </div>
              </div>
              
              {quote.hs_terms && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-extrabold mb-3">Termos e Condições</h4>
                  <div className="bg-muted p-4 rounded-md text-sm">
                    {quote.hs_terms.includes('<div') || quote.hs_terms.includes('<p') ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(quote.hs_terms) }} />
                    ) : (
                      quote.hs_terms.split('\n').map((line, index) => {
                        const termMatch = line.match(/^([a-z]|[0-9]+)\.\s(.+)/i);
                        
                        if (termMatch) {
                          return (
                            <div key={index} className={index > 0 ? 'mt-2' : ''} style={{ display: 'flex' }}>
                              <span className="font-extrabold mr-2">{termMatch[1]}.</span>
                              <span>{termMatch[2]}</span>
                            </div>
                          );
                        }
                        
                        return (
                          <p key={index} className={index > 0 ? 'mt-2' : ''}>
                            {line}
                          </p>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              
              {quote.hs_comments && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-extrabold mb-3">Comentários</h4>
                  <div className="bg-muted p-4 rounded-md text-sm">
                    {quote.hs_comments.includes('<div') || quote.hs_comments.includes('<p') ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(quote.hs_comments) }} />
                    ) : (
                      quote.hs_comments.split('\n').map((line, index) => (
                        <p key={index} className={index > 0 ? 'mt-2' : ''}>
                          {line}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Assinatura Eletrônica */}
          {quote?.hs_esign_enabled && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-xl flex items-center">
                  <User className="mr-2 h-5 w-5 text-muted-foreground" />
                  Assinatura Eletrônica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Status</h4>
                    <p className="font-extrabold">
                      {quote?.hs_esign_num_signers_completed || 0} de {quote?.hs_esign_num_signers_required || 0} assinaturas
                    </p>
                  </div>
                  
                  {quote?.hs_esign_date && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-extrabold text-muted-foreground">Data de Assinatura</h4>
                      <p className="font-extrabold">{new Date(quote.hs_esign_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  
                  {quote?.hs_esign_verification_method && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-extrabold text-muted-foreground">Método de Verificação</h4>
                      <p className="font-extrabold">{quote.hs_esign_verification_method}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cliente */}
          {dealData?.customer && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center">
                  <Building className="mr-2 h-5 w-5 text-muted-foreground" />
                  Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Nome</h4>
                  <p className="font-extrabold">{dealData.customer.name}</p>
                </div>
                
                {dealData.customer.business_identification_number && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">CNPJ</h4>
                    <p className="font-extrabold">{dealData.customer.business_identification_number}</p>
                  </div>
                )}
                
                {dealData.customer.industry && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Indústria/Segmento</h4>
                    <p className="font-extrabold">{dealData.customer.industry}</p>
                  </div>
                )}
                
                {dealData.customer.address && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Endereço</h4>
                    <p className="font-extrabold">
                      {dealData.customer.address}
                      {dealData.customer.city && `, ${dealData.customer.city}`}
                      {dealData.customer.state && `, ${dealData.customer.state}`}
                      {dealData.customer.zip && ` - ${dealData.customer.zip}`}
                    </p>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full mt-2" 
                  onClick={() => navigate(`/customers/${dealData.customer.id}`)}
                >
                  <Building className="mr-2 h-4 w-4" />
                  Ver Perfil Completo
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* Oportunidade */}
          {dealData && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center">
                  <DollarSign className="mr-2 h-5 w-5 text-muted-foreground" />
                  Oportunidade
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-muted-foreground">Nome</h4>
                  <p className="font-extrabold">{dealData.dealname}</p>
                </div>
                
                {dealData.amount && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Valor</h4>
                    <p className="font-extrabold">{formatCurrency(dealData.amount, dealData.deal_currency_code || 'BRL')}</p>
                  </div>
                )}
                
                {dealData.dealstage && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Etapa</h4>
                    <p className="font-extrabold">{dealData.dealstage}</p>
                  </div>
                )}
                
                {dealData.owner_name && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-extrabold text-muted-foreground">Responsável</h4>
                    <p className="font-extrabold">{dealData.owner_name}</p>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => navigate(`/business-opportunities/${dealData.id}`)}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Ver Oportunidade
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* Ações */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center">
                Ações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" onClick={handleOpenContractModal}>
                <FileText className="mr-2 h-4 w-4" />
                Gerar Contrato
              </Button>
              
              <Button variant="outline" className="w-full justify-start" onClick={() => window.open(`/public-proposal/${quote.id}`, '_blank')}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Ver Página Pública
              </Button>
              
              {quote.hs_pdf_download_link && (
                <Button variant="outline" className="w-full justify-start" onClick={downloadPdf}>
                  <Download className="mr-2 h-4 w-4" />
                  Baixar PDF
                </Button>
              )}
              
              {quote.hs_quote_link && (
                <Button variant="outline" className="w-full justify-start" onClick={openQuoteUrl}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Ver no Hubspot
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal para criação de contrato */}
      <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Gerar Contrato</DialogTitle>
            <DialogDescription>
              Selecione um template e defina o título do contrato para esta cotação.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">
                Título
              </Label>
              <Input
                id="title"
                value={contractTitle}
                onChange={(e) => setContractTitle(e.target.value)}
                className="col-span-3"
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="template" className="text-right">
                Template
              </Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione um template" />
                </SelectTrigger>
                <SelectContent>
                  {contractTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedTemplate && (
              <div className="text-sm text-muted-foreground mt-2">
                <p className="font-extrabold">Descrição do template:</p>
                <p>{contractTemplates.find(t => t.id === selectedTemplate)?.description || 'Sem descrição'}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateContract} 
              disabled={!selectedTemplate || !contractTitle || isCreatingContract}
            >
              {isCreatingContract ? 'Gerando...' : 'Gerar Contrato'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalDetail; 